import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid2,
    Card,
    Link,
    Divider,
    CircularProgress
} from "@mui/material";
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import UploadStatusModal from "../Components/UploadStatusModal";
import CustomIcon from "../../../Components/CustomIcon";
import { downloadFullReport, downloadErrorReport } from "../../../Api/ResourceOnboardingApi";
import DownloadCSV from "../../../Helper/CSVOperations/DownloadCSV";

const Step5UploadComplete = ({ selectedResourceType, uploadData }) => {
    const [showModal, setShowModal] = useState(true);
    const [downloadingFull, setDownloadingFull] = useState(false);
    const [downloadingError, setDownloadingError] = useState(false);

    // Extract data from uploadData
    const userRemarks = uploadData?.userRemarks || "N/A";
    const numTotalRecords = uploadData?.numTotalRecords || 0;
    const numCreatedRecords = uploadData?.numCreatedRecords || 0;
    const numUpdatedRecords = uploadData?.numUpdatedRecords || 0;
    const numFailedRecords = uploadData?.numFailedRecords || 0;
    const executionTime = uploadData?.executionTime; // Can be undefined
    const importUUID = uploadData?.importUUID;

    const uploadedRecordsCount = numCreatedRecords + numUpdatedRecords;

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleDownloadFullReport = async (e) => {
        e.preventDefault();
        if (!importUUID || downloadingFull) return;

        try {
            setDownloadingFull(true);
            const response = await downloadFullReport(importUUID);

            // Check if response is blob data (file download)
            if (response?.response instanceof Blob) {
                const url = window.URL.createObjectURL(response?.response);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${selectedResourceType?.label || 'Resource'}_Full_Report_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                // Fallback to CSV data format
                const csvData = response?.data || [];
                if (csvData.length > 0) {
                    const filename = `${selectedResourceType?.label || 'Resource'}_Full_Report_${new Date().toISOString().split('T')[0]}.csv`;
                    DownloadCSV(filename, csvData);
                }
            }
        } catch (error) {
            console.error("Error downloading full report:", error);
        } finally {
            setDownloadingFull(false);
        }
    };

    const handleDownloadErrorReport = async (e) => {
        e.preventDefault();
        if (!importUUID || downloadingError || numFailedRecords === 0) return;

        try {
            setDownloadingError(true);
            const response = await downloadErrorReport(importUUID);

            // Check if response is blob data (file download)
            if (response?.response instanceof Blob) {
                const url = window.URL.createObjectURL(response?.response);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${selectedResourceType?.label || 'Resource'}_Error_Report_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                // Fallback to CSV data format
                const csvData = response?.data || [];
                if (csvData.length > 0) {
                    const filename = `${selectedResourceType?.label || 'Resource'}_Error_Report_${new Date().toISOString().split('T')[0]}.csv`;
                    DownloadCSV(filename, csvData);
                }
            }
        } catch (error) {
            console.error("Error downloading error report:", error);
        } finally {
            setDownloadingError(false);
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3.5 }}>
                <Typography variant="fw500s20c000000" sx={{ lineHeight: '20px' }}>
                    {selectedResourceType?.label || 'Resource Onboarding'}
                </Typography>
            </Box>

            {/* Upload Summary Section */}
            <Grid2 container flexDirection={'column'} gap={1} sx={{ mb: 3 }}>
                <Typography variant="fw500s16c000000" sx={{ lineHeight: '16px' }}>
                    Upload Summary
                </Typography>
                <Typography variant="fw500s14c000000" sx={{ lineHeight: '14px', color: '#686868' }}>
                    Your data has been uploaded in our system successfully
                </Typography>
            </Grid2>

            {/* Remark Card */}
            <Card sx={{
                py: 2,
                px: 3,
                mb: 1,
                backgroundColor: '#F7F7FF',
                border: '1px solid #D4D5FC',
                borderRadius: '12px',
                boxShadow: 'none'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <CustomIcon name="comment-icon" fill="#6D72F6" />
                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                        <Typography variant="fw600s14c000000" sx={{ color: '#2E2E2E', mb: 0.5 }}>
                            Remark:
                        </Typography>
                        <Typography variant="fw500s14c000000">
                            {userRemarks}
                        </Typography>
                    </Box>
                </Box>
            </Card>

            {/* Summary Details */}
            <Box sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: '#F9F9F9',
                py: 2.5,
                px: 6,
            }}>
                {/* Total Records */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2.5,
                    px: 1.5
                }}>
                    <Typography variant="fw500s16c000000">
                        Total Records:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Link
                            href="#"
                            underline="always"
                            onClick={handleDownloadFullReport}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: '#494949',
                                fontSize: '14px',
                                fontWeight: 500,
                                textDecorationColor: '#494949',
                                cursor: downloadingFull ? 'wait' : 'pointer',
                                opacity: downloadingFull ? 0.6 : 1,
                                pointerEvents: downloadingFull || !importUUID ? 'none' : 'auto'
                            }}
                        >
                            {downloadingFull ? (
                                <CircularProgress size={16} sx={{ color: '#494949' }} />
                            ) : (
                                <FileDownloadOutlinedIcon sx={{ fontSize: '16px' }} />
                            )}
                            Download Full Data Report
                        </Link>
                        <Typography variant="fw500s16c000000" sx={{ minWidth: '30px', textAlign: 'right' }}>
                            {numTotalRecords}
                        </Typography>
                    </Box>
                </Box>

                {/* Records Created */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2.5,
                    px: 1.5,
                    borderTop: '1px solid #E6E6E6'
                }}>
                    <Typography variant="fw500s16c000000">
                        Records Created:
                    </Typography>
                    <Typography variant="fw500s16c000000">
                        {numCreatedRecords}
                    </Typography>
                </Box>

                {/* Records Updated */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2.5,
                    px: 1.5,
                    borderTop: '1px solid #E6E6E6'
                }}>
                    <Typography variant="fw500s16c000000">
                        Records Updated:
                    </Typography>
                    <Typography variant="fw500s16c000000">
                        {numUpdatedRecords}
                    </Typography>
                </Box>

                {/* Failed Records */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2.5,
                    px: 1.5,
                    borderTop: '1px solid #E6E6E6'
                }}>
                    <Typography variant="fw500s16c000000">
                        Failed Records:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Link
                            href="#"
                            underline="always"
                            onClick={handleDownloadErrorReport}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: '#494949',
                                fontSize: '14px',
                                fontWeight: 500,
                                textDecorationColor: '#494949',
                                cursor: downloadingError ? 'wait' : 'pointer',
                                opacity: downloadingError || numFailedRecords === 0 ? 0.6 : 1,
                                pointerEvents: downloadingError || !importUUID || numFailedRecords === 0 ? 'none' : 'auto'
                            }}
                        >
                            {downloadingError ? (
                                <CircularProgress size={16} sx={{ color: '#494949' }} />
                            ) : (
                                <FileDownloadOutlinedIcon sx={{ fontSize: '16px' }} />
                            )}
                            Download Error Report
                        </Link>
                        <Typography variant="fw500s16c000000" sx={{ color: '#B50500', minWidth: '30px', textAlign: 'right' }}>
                            {numFailedRecords}
                        </Typography>
                    </Box>
                </Box>

                {/* Execution Time - Only show if available */}
                {executionTime && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 2.5,
                        px: 1.5,
                        borderTop: '1px solid #E6E6E6'
                    }}>
                        <Typography variant="fw600s16c000000">
                            Execution Time:
                        </Typography>
                        <Typography variant="fw500s16c000000">
                            {executionTime}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Upload Status Modal */}
            <UploadStatusModal
                open={showModal}
                onClose={handleCloseModal}
                uploadedRecordsCount={uploadedRecordsCount}
            />
        </Box>
    );
};

export default Step5UploadComplete;
